import { createSlice } from '@reduxjs/toolkit'
import { keyBy, setNestedKey } from '../../../helpers/common'
import { register } from '../../../helpers/reducerRegistry'
import { TIME_PERIOD } from '../constant'
import { convertDataCriteria } from '../helper'
import {
  getAlertCriteriaDynamic,
  getAlertHighlight,
  getAlertList,
  searchCompany,
  searchIndustry,
} from './thunk'

const initialState = {
  filter: {
    organizationId: null,
    industryId: null,
    readStatus: [],
    subjectType: [],
    sentiment: [],
    timePeriod: TIME_PERIOD.ALL.key,
    startDate: TIME_PERIOD.ALL.startDate,
    endDate: TIME_PERIOD.ALL.endDate,
    criteria: [],
    page: 1,
    pageSize: 20,
    triggerChange: false,
  },

  highlight: {},
  criteria: {},

  alertIds: [],
  alertIdsAll: [],
  alertById: {},
  totalRecords: 0,
  totalPages: 1,

  searchTerm: '',
  searchSubject: [],

  checkedIds: {},

  loading: {
    highlight: true,
    criteria: true,
    alertList: true,
    searchSubject: true,
  },
}

export const slice = createSlice({
  name: 'alerts/alertList',
  initialState,
  reducers: {
    resetStoreAlertList: () => initialState,
    resetData: (state) => {
      state.highlight = initialState.highlight
      state.alertIds = initialState.alertIds
      // state.checkedIds = initialState.checkedIds
      state.loading.highlight = initialState.loading.highlight
      state.loading.alertList = initialState.loading.alertList
    },
    changeFilter: (state, action) => {
      state.filter = { ...state.filter, ...action.payload }
    },
    changeSearchTerm: (state, action) => {
      state.searchTerm = action.payload
    },
    resetFilter: (state) => {
      state.filter = {
        ...initialState.filter,
        organizationId: state.filter.organizationId,
        industryId: state.filter.industryId,
      }
    },
    changeCheckedIds: (state, { payload }) => {
      state.checkedIds = { ...state.checkedIds, ...payload }
    },
    changeAlertById: (state, { payload = [] }) => {
      payload.forEach((element) => {
        const { path, data } = element
        setNestedKey(state.alertById, path, data)
      })
    },
    changeLoading: (state, { payload }) => {
      state.loading = { ...state.loading, ...payload }
    },
  },
  extraReducers: (builder) => {
    // getAlertHighlight
    builder.addCase(getAlertHighlight.pending, (state) => {
      state.loading.highlight = true
    })
    builder.addCase(getAlertHighlight.fulfilled, (state, action) => {
      state.highlight = action.payload || {}
      state.loading.highlight = false
    })
    builder.addCase(getAlertHighlight.rejected, (state, action) => {
      state.loading.highlight = action.payload.loading
    })
    //getAlertCriteriaDynamic
    builder.addCase(getAlertCriteriaDynamic.pending, (state) => {
      state.loading.criteria = true
    })
    builder.addCase(getAlertCriteriaDynamic.fulfilled, (state, action) => {
      state.criteria = convertDataCriteria(action.payload)
      state.loading.criteria = false
    })
    builder.addCase(getAlertCriteriaDynamic.rejected, (state, action) => {
      state.loading.criteria = action.payload.loading
    })
    // getAlertList
    builder.addCase(getAlertList.pending, (state) => {
      state.loading.alertList = true
    })
    builder.addCase(getAlertList.fulfilled, (state, { payload }) => {
      state.alertIdsAll = [
        ...new Set([
          ...state.alertIds,
          ...payload.data.map((item) => item.recordId),
        ]),
      ]
      state.alertIds = payload.data.map((item) => item.recordId)
      state.alertById = keyBy(payload.data, 'recordId')
      state.totalRecords = payload.totalRecords
      state.totalPages = Math.ceil(state.totalRecords / state.filter.pageSize)
      state.loading.alertList = false
    })
    builder.addCase(getAlertList.rejected, (state, action) => {
      state.alertIds = []
      state.alertById = {}
      state.totalRecords = 0
      state.totalPages = 1
      state.loading.alertList = action.payload.loading
    })
    // searchCompany
    builder.addCase(searchCompany.pending, (state) => {
      state.loading.searchSubject = true
    })
    builder.addCase(searchCompany.fulfilled, (state, action) => {
      state.searchSubject = action.payload
      state.loading.searchSubject = false
    })
    builder.addCase(searchCompany.rejected, (state, action) => {
      state.loading.searchSubject = action.payload.loading
    })
    // searchIndustry
    builder.addCase(searchIndustry.pending, (state) => {
      state.loading.searchSubject = true
    })
    builder.addCase(searchIndustry.fulfilled, (state, action) => {
      state.searchSubject = action.payload
      state.loading.searchSubject = false
    })
    builder.addCase(searchIndustry.rejected, (state, action) => {
      state.loading.searchSubject = action.payload.loading
    })
  },
})

export const selectFilter = (type, callback) => (state) => {
  const filter = state[slice.name].filter
  const data = type ? filter[type] : filter
  return callback ? callback(data) : data
}

export const selectHighlight = (state) => state[slice.name].highlight
export const selectCriteria = (state) => state[slice.name].criteria

export const selectAlertIds = (state) => state[slice.name].alertIds
export const selectAlertIdsAll = (state) => state[slice.name].alertIdsAll
export const selectAlertById = (id, attr) => (state) => {
  const data = state[slice.name].alertById
  if (!id) return data
  if (!attr) return data[id]
  return data[id]?.[attr]
}
export const selectTotalRecords = (state) => state[slice.name].totalRecords
export const selectTotalPages = (state) => state[slice.name].totalPages

export const selectSearchTerm = (state) => state[slice.name].searchTerm
export const selectSearchSubject = (state) => state[slice.name].searchSubject

export const selectCheckedIds = (key, callback) => (state) => {
  const checkedIds = state[slice.name].checkedIds
  const data = key ? checkedIds[key] : checkedIds
  return callback ? callback(data) : data
}

export const selectLoading = (type) => (state) => {
  const loadingObj = state[slice.name].loading
  if (!type) return Object.values(loadingObj).some(Boolean)
  return state[slice.name].loading[type]
}

export const {
  resetStoreAlertList,
  resetData,
  resetFilter,
  changeFilter,
  changeCheckedIds,
  changeAlertById,
  changeSearchTerm,
  changeLoading,
} = slice.actions

register(slice.name, slice.reducer)
